import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Dropdown, Card } from 'pepsico-ds';
import { TextField, Autocomplete, Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import {
  Brand_strategy,
  dropdownOptionColumnNames,
  masterActionOptions,
  masterConditionOptions,
  masterQuillAutoCompleteOptions,
  updateLogsColumns,
  weightDropdownOptions
} from '../../utils/Constants';
import { useOkta } from '../../hooks/oktaHook';
import { getOptionsInStructuredFormat, removeNullValuesFromObj } from '../../utils/Utils';
import {
  applyConditionsAndActions,
  fetchImpactedSkusCount,
  fetchMasterFillDropdownOptionsData,
  getQuickFillLogsData
} from './services';
import './MasterDataQuickFill.css';
import WeightsModal from './WeightsModal';
import { triggerNotification } from '../../utils/NotificationContext';
import ConfirmationModal from '../../components/Modals/ConfirmationModal';
import DynamicTable from '../../components/Table/Table';

const MasterDataQuickFill = (props) => {
  const [conditionFormInputs, setConditionFormInputs] = useState([{ id: 1 }]);
  const [actionFormInputs, setActionFormInputs] = useState([{ id: 1 }]);
  const [selectedMultiOptions, setSelectedMultiOptions] = useState({});
  const [conditionDropdownData, setConditionDropdownData] = useState({});
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [logsData, setLogsData] = useState([]);
  const [skusCount, setSkusCount] = useState(0);
  const [showWeightsModal, setShowWeightsModal] = useState(false);
  const [weightInputs, setWeightInputs] = useState([]);
  const { userDetails } = useOkta();
  const { email } = userDetails;

  const selectedBu = useSelector((state) => state?.cycle?.selectedBu);

  const addConditionsForm = () => {
    if (conditionFormInputs?.length < Object.keys(conditionDropdownData)?.length) {
      let ids = conditionFormInputs?.map((item) => item.id);
      const maxId = Math.max(...ids);
      setConditionFormInputs([...conditionFormInputs, ...[{ id: maxId + 1 }]]);
    }
  };

  const addActionForm = () => {
    if (actionFormInputs?.length < 11) {
      let ids = actionFormInputs?.map((item) => item.id);
      const maxId = Math.max(...ids);
      setActionFormInputs([...actionFormInputs, ...[{ id: maxId + 1 }]]);
    }
  };

  const handleDeleteFormInput = (id) => {
    getImpactedSkusCount(conditionFormInputs?.filter((item) => item.id !== id));
    setConditionFormInputs(conditionFormInputs?.filter((item) => item.id !== id));
    let multiOptions = { ...selectedMultiOptions };
    delete multiOptions[id];
    setSelectedMultiOptions(multiOptions);
  };

  const handleDeleteActionFormInput = (id) => {
    setActionFormInputs(actionFormInputs?.filter((item) => item.id !== id));
  };

  const handleChange = (value, id, optionType) => {
    let data = optionType === 'action' ? actionFormInputs : conditionFormInputs;
    let filteredData = data?.map((item) => {
      if (item.id === id) {
        if (value === undefined) {
          item = { id: item.id };
        } else {
          item = { id: item.id, [value]: optionType === 'action' ? '' : [] };
        }
      }
      return item;
    });
    if (optionType === 'action') {
      setActionFormInputs(filteredData);
    } else {
      setConditionFormInputs(filteredData);
      selectedMultiOptions[id] = [];
      setSelectedMultiOptions({ ...selectedMultiOptions });
    }
  };

  const handleDropdownChange = (selectedValue, itemObj, optionType, textType = 'text') => {
    let data = optionType === 'action' ? actionFormInputs : conditionFormInputs;
    let filteredData = data?.map((item) => {
      let key = Object.keys(itemObj)?.[1];
      if (item.id === itemObj?.id) {
        if (selectedValue === undefined) {
          item = {
            id: item.id,
            [key]: optionType === 'action' ? '' : []
          };
        } else {
          item = {
            id: item.id,
            [key]: optionType === 'action' ? selectedValue : selectedValue?.map((item) => item.id)
          };
        }
      }
      return item;
    });
    if (optionType === 'action') {
      if (textType === 'text') {
        setActionFormInputs(filteredData);
      } else {
        if (/^\d*$/.test(selectedValue)) setActionFormInputs(filteredData);
      }
    } else {
      getImpactedSkusCount(filteredData);
      setSelectedMultiOptions({ ...selectedMultiOptions, ...{ [itemObj.id]: selectedValue } });
      setConditionFormInputs(filteredData);
    }
  };

  const getStructuredFormatData = (data) => {
    let filteredList = data?.reduce((acc, obj) => {
      const key = Object.keys(obj).find((key) => key !== 'id');
      acc[key] = obj[key];
      return acc;
    }, {});
    return filteredList;
  };

  const getImpactedSkusCount = async (data) => {
    let payload = await getStructuredFormatData(data);
    fetchImpactedSkusCount(selectedBu, payload).then((count) => {
      setSkusCount(count);
    });
  };

  const clearSelection = () => {
    setConditionFormInputs([{ id: 1 }]);
    setActionFormInputs([{ id: 1 }]);
    setSelectedMultiOptions({});
    setSkusCount(0);
    setWeightInputs([]);
  };

  const saveConditionsAndActions = async () => {
    let conditionsData = await getStructuredFormatData(conditionFormInputs);
    let actionData = await getStructuredFormatData(actionFormInputs);
    let weightsData = await getStructuredFormatData(weightInputs);
    let payloadObj = {
      Conditions: conditionsData,
      Actions: { ...actionData, ...weightsData }
    };
    applyConditionsAndActions(selectedBu, email, payloadObj, skusCount).then(() => {
      clearSelection();
      setShowConfirmationModal(false);
      getUpdatedDropdownLists();
    });
  };

  const handleLogsUpdate = (data) => {
    let conditionsForm = removeNullValuesFromObj(data?.view?.Conditions);
    let actionsForm = removeNullValuesFromObj(data?.view?.Actions);
    let weightOptions = weightDropdownOptions?.map((item) => item.id);
    let conditionFormObject = getOptionsInStructuredFormat(conditionsForm);
    let conditionFormArr = [];
    let actionsFormArr = [];
    let weightsArr = [];
    let selectedOptions = {};
    Object.keys(conditionsForm)?.map((key, index) => {
      selectedOptions = { ...selectedOptions, ...{ [index + 1]: conditionFormObject[key] } };
      conditionFormArr = [...conditionFormArr, ...[{ id: index + 1, [key]: conditionsForm[key] }]];
      return key;
    });
    Object.keys(actionsForm)?.map((key, index) => {
      if (weightOptions?.includes(key)) {
        weightsArr = [
          ...weightsArr,
          ...[{ id: weightOptions?.indexOf(key) + 1, [key]: actionsForm[key] }]
        ];
      } else {
        actionsFormArr = [...actionsFormArr, ...[{ id: index + 1, [key]: actionsForm[key] }]];
      }
      return key;
    });
    setConditionFormInputs(conditionFormArr);
    getImpactedSkusCount(conditionFormArr);
    setSelectedMultiOptions((prevState) => ({ ...prevState, ...selectedOptions }));
    setActionFormInputs(actionsFormArr);
    setWeightInputs(weightsArr);
  };

  const handleApply = async () => {
    let actionData = await getStructuredFormatData(actionFormInputs);
    let keys = Object.keys(actionData);
    if (keys?.includes('Launch Period') || keys?.includes('Discontinued Period')) {
      let launchPeriodValid = null;
      let discountinuedPeriodValid = null;
      if (keys?.includes('Launch Period')) {
        // Regular expression to match the pattern: 2000-2100 (first 4 digits) and 01-13 (last 2 digits)
        const regex = /^(20[0-9]{2}|2100)(0[1-9]|1[0-3])$/;
        // Check if the input matches the regex
        launchPeriodValid = regex.test(Number(actionData?.['Launch Period']));
      }
      if (keys?.includes('Discontinued Period')) {
        // Regular expression to match the pattern: anything (first 4 digits) and 01-13 (last 2 digits)
        let regexPeriod = /^\d{4}(0[1-9]|1[0-3])$/;
        discountinuedPeriodValid = regexPeriod.test(Number(actionData?.['Discontinued Period']));
      }
      if (launchPeriodValid === false) {
        triggerNotification(
          'For Launch Period YYYY should be between 2000-2100 and PP should be 1-13',
          'info'
        );
      } else if (discountinuedPeriodValid === false) {
        triggerNotification(' For Discontinued Period PP Range should be between 1 and 13', 'info');
      } else {
        setShowConfirmationModal(true);
      }
    } else {
      setShowConfirmationModal(true);
    }
  };

  const getNewFilteredOptions = (allOptions, optionType, itemObj) => {
    let data = optionType === 'action' ? actionFormInputs : conditionFormInputs;
    let arr = [];
    data?.map((item) => {
      arr = [...arr, ...Object.keys(item)];
      return item;
    });
    let filteredArr = arr?.filter((li) => li !== 'id');
    let value = Object.keys(itemObj)?.length > 1 ? Object.keys(itemObj)?.[1] : '';
    return [
      ...[{ displayText: value, id: value }],
      ...(allOptions?.filter((option) => !filteredArr?.includes(option.displayText)) ?? [])
    ]?.filter((item) => item.id !== '');
  };

  const getUpdatedDropdownLists = () => {
    fetchMasterFillDropdownOptionsData(selectedBu).then((resp) =>
      setConditionDropdownData(getOptionsInStructuredFormat(resp))
    );
    setTimeout(() => {
      getQuickFillLogsData(selectedBu).then((resp) => setLogsData(resp));
    }, 1000);
  };

  useEffect(() => {
    getUpdatedDropdownLists();
  }, []);

  const getSelectedValue = (value, item = null) => {
    let newValue =
      item !== null
        ? item[value] !== ''
          ? [{ displayText: item[value], id: item[value] }]
          : []
        : value
          ? [{ displayText: value, id: value }]
          : [];
    return newValue;
  };

  const showDropdown = (item) => {
    return Object.keys(dropdownOptionColumnNames)?.includes(Object.keys(item)?.[1]);
  };

  const applyButtonDisabled = () => {
    let disableButton = false;
    conditionFormInputs?.forEach((itemA) => {
      const keys = Object.keys(itemA);
      if (keys?.length !== 2) {
        disableButton = true;
      }
      if (itemA[keys[1]]?.length === 0) {
        disableButton = true;
      }
    });
    actionFormInputs.forEach((itemB) => {
      const keys = Object.keys(itemB);
      if (keys?.length !== 2) {
        disableButton = true;
      }
      if (itemB[keys?.[1]] === null || itemB[keys?.[1]] === '') {
        disableButton = true;
      }
    });
    return disableButton;
  };

  return (
    <>
      <Card ellipsis={false} icon="">
        <h3 className="card-title-header" data-testid="card-title">
          Master Update
        </h3>
        {conditionFormInputs?.map((item) => (
          <div
            key={item.id}
            className="input-conditions-container"
            style={{
              margin: item.id === 1 ? '20px 40px' : '20px'
            }}>
            <strong data-testid="if-text">{item.id === 1 ? 'If' : 'And'}</strong>
            <Dropdown
              data-testid="condition-dropdown"
              className="action-single-select"
              childList={getNewFilteredOptions(masterConditionOptions, 'condition', item)}
              isClearable
              placeholder="Select"
              selection="single"
              selectedValue={getSelectedValue(Object.keys(item)?.[1])}
              setSelectedValue={(value) =>
                handleChange(value?.[0]?.displayText, item.id, 'condition')
              }
            />
            <strong>{'Is'}</strong>
            <Dropdown
              key={Object.keys(item)?.[1]}
              data-testid="condition-dropdown-multi"
              childList={conditionDropdownData[Object.keys(item)?.[1]] || []}
              isClearable
              placeholder="Select"
              selection="multiple"
              selectedValue={selectedMultiOptions?.[item.id] || []}
              setSelectedValue={(value) => handleDropdownChange(value, item, 'condition')}
              style={{ margin: '0px 10px' }}
            />
            {item.id === 1 ? (
              <div className="sku-count-container">
                <InfoOutlinedIcon /> {`${skusCount} SKUs impacted`}
              </div>
            ) : (
              <CloseIcon
                data-testid="condition-close-icon"
                className="close-icon"
                onClick={() => handleDeleteFormInput(item.id)}
              />
            )}
          </div>
        ))}
        <Button
          data-testid="add-condition-btn"
          variant="primary"
          className="button-font-size-12"
          onClick={() => addConditionsForm()}>
          + Add Condition
        </Button>
      </Card>
      <Card ellipsis={false} icon="">
        <h3 className="card-title-header">Action</h3>
        {actionFormInputs?.map((item) => (
          <div className="input-conditions-container" key={item.id} style={{ margin: '20px 10px' }}>
            <strong>Then</strong>
            <Dropdown
              data-testid="action-dropdown"
              childList={getNewFilteredOptions(masterActionOptions, 'action', item)}
              className="action-single-select"
              isClearable
              placeholder="Select"
              selection="single"
              selectedValue={getSelectedValue(Object.keys(item)?.[1])}
              setSelectedValue={(value) => handleChange(value?.[0]?.displayText, item.id, 'action')}
            />
            <strong>Is</strong>
            {showDropdown(item) ? (
              <Dropdown
                data-testid="action-dropdown-value"
                className="action-single-select-value"
                childList={dropdownOptionColumnNames[Object.keys(item)?.[1]]}
                isClearable
                placeholder="Select"
                selection="single"
                selectedValue={getSelectedValue(Object.keys(item)?.[1], item) || []}
                setSelectedValue={(value) =>
                  handleDropdownChange(value?.[0]?.displayText, item, 'action')
                }
                style={{
                  margin: '0px 10px'
                }}
              />
            ) : masterQuillAutoCompleteOptions?.includes(Object.keys(item)?.[1]) ? (
              <Autocomplete
                data-testid="autocomplete"
                freeSolo
                value={item[Object.keys(item)?.[1]] || ''}
                onChange={(event, newValue) => handleDropdownChange(newValue, item, 'action')}
                inputValue={item[Object.keys(item)?.[1]] || ''}
                onInputChange={(event, newInputValue) =>
                  handleDropdownChange(newInputValue, item, 'action')
                }
                options={
                  conditionDropdownData[Object.keys(item)?.[1]]?.map((item) => item.id) || []
                }
                renderInput={(params) => <TextField {...params} />}
                sx={{
                  width: '240px',
                  height: 32,
                  marginLeft: '10px',
                  '& .MuiInputBase-root': {
                    height: 32 // Adjust the height of the input here
                  }
                }}
              />
            ) : (
              <TextField
                data-testid="action-input"
                className="action-input"
                sx={{
                  '& .MuiInputBase-root': {
                    height: 32 // Adjust the height of the input here
                  }
                }}
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric', // Hints to mobile devices to show a numeric keyboard
                  pattern: '[0-9]*' // Restrict input to digits
                }}
                value={item[Object.keys(item)?.[1]] || ''}
                placeholder={
                  Object.keys(item)?.includes('Launch Period') ||
                  Object.keys(item)?.includes('Discontinued Period')
                    ? 'YYYYPP'
                    : ''
                }
                onChange={(event) =>
                  handleDropdownChange(event.target.value, item, 'action', 'numeric')
                }
              />
            )}
            {item.id !== 1 && (
              <CloseIcon
                data-testid="action-close-icon"
                className="close-icon"
                onClick={() => handleDeleteActionFormInput(item.id)}
                style={{ marginLeft: showDropdown(item) ? '30px' : '20px' }}
              />
            )}
            {Object.keys(item)?.[1] === Brand_strategy && (
              <span className="weights-text" onClick={() => setShowWeightsModal(true)}>
                {`${weightInputs?.length === 0 ? 'Set' : 'Edit'}`} Weights
              </span>
            )}
          </div>
        ))}
        <Button
          variant="primary"
          data-testid="add-action-btn"
          className="button-font-size-12"
          onClick={() => addActionForm()}>
          + Add Action
        </Button>
      </Card>
      <div className="submit-container">
        <Button
          data-testid="clear-selection-btn"
          variant="secondary"
          className="button-font-size-16"
          onClick={() => clearSelection()}>
          Clear Selection
        </Button>
        <Button
          variant="primary"
          data-testid="apply-btn"
          className="button-font-size-16"
          disabled={applyButtonDisabled()}
          onClick={() => handleApply()}>
          Apply
        </Button>
      </div>
      <Box
        component="section"
        sx={{
          p: 2,
          borderRadius: '8px',
          backgroundColor: '#FBFCFE',
          boxShadow: '4px 0px 24px -2px #005CBC14'
        }}>
        <DynamicTable
          data-testid="logs-table"
          originalColumns={updateLogsColumns}
          originalData={logsData}
          tableName="Update Log"
          isCollapseButton={false}
          isSearch={false}
          handleLogsData={(data) => handleLogsUpdate(data)}
        />
      </Box>
      <WeightsModal
        weightInputs={weightInputs}
        showModal={showWeightsModal}
        handleClose={() => setShowWeightsModal(false)}
        saveWeightInputs={(data) => setWeightInputs(data)}
      />
      <ConfirmationModal
        open={showConfirmationModal}
        title={`Confirm Application to ${skusCount} SKUs`}
        text={`Are you sure you want to update all the SKUs that are impacted.`}
        descriptionText={`${skusCount} SKUs impacted`}
        cancelText="Cancel"
        SubmitText={'Apply'}
        onClose={() => {
          setShowConfirmationModal(false);
        }}
        onSubmit={() => saveConditionsAndActions()}
      />
    </>
  );
};

export default MasterDataQuickFill;
